import { defineReceiver, defineCommand } from '@drapejs/invoker';
import { request, query, sales } from '@motillo/drapejs-litium';
import { gql } from 'graphql-request';

export const commands = {
  addGiftToCart: defineCommand<{
    url: string;
    articleNumber: string;
  }>('addGiftToCart'),
  placeGetMyGiftOrder: defineCommand<{
    url: string;
    email: string;
    firstName: string;
    lastName: string;
    postalCode: string;
    address1: string;
    address2: string;
    city: string;
    phone: string;
    country: string;
    servicePointId: string;
    addressCode: string;
    subscribedToNewsletter: boolean;
  }>('placeGetMyGiftOrder'),
  getOrder: defineCommand<{
    url: string;
    orderId: string;
  }>('getOrder'),
  addBackInStockAlert: defineCommand<{
    url: string;
    email: string;
    variantId: string;
  }>('addBackInStockAlert'),
  getOrderTracking: defineCommand<{
    url: string;
    trackingId: string;
  }>('getOrderTracking'),
};

export const receivers = {
  addGiftToCart: defineReceiver(
    commands.addGiftToCart,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(
          gql`
            mutation addGiftToCart($url: String!, $articleNumber: String!) {
              session(url: $url) {
                addGiftToCart {
                  addGiftToCart(articleNumber: $articleNumber) {
                    row {
                      id
                      variantName
                      description
                      articleNumber
                      quantity
                      unitPrice
                      unitListPrice
                      totalPrice
                      url
                      imageId
                    }
                    cart {
                      ...CartFields
                    }
                    error
                    }
                  }
                }
              }
            `
          ,
          ...sales.withCartFields()
        ),
        {
          url: `${(<any>command).protocol}//${(<any>command).host}${(<any>command).path}`,
          articleNumber: command.articleNumber || '',
        }
      );

      await this.cache.setItem('__cart', result.data.session.addGiftToCart.addGiftToCart.cart);
      return result.data.session.addGiftToCart.addGiftToCart;
    },
    'litium'
  ),
  placeGetMyGiftOrder: defineReceiver(
    commands.placeGetMyGiftOrder,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(
          gql`
            mutation placeGetMyGiftOrder(
              $url: String
              $email: String
              $firstName: String
              $lastName: String
              $postalCode: String
              $address1: String
              $address2: String
              $city: String
              $phone: String
              $country: String!
              $addressCode: String
              $subscribedToNewsletter: Boolean
            ) {
              session(url: $url) {
                placeGetMyGiftOrder{
                  placeGetMyGiftOrder(
                    email: $email
                    firstName: $firstName
                    lastName: $lastName
                    postalCode: $postalCode
                    address1: $address1
                    address2: $address2
                    city: $city
                    phone: $phone
                    country: $country
                    addressCode: $addressCode
                    subscribedToNewsletter: $subscribedToNewsletter
                  ) {
                    error
                    cart {
                      ...CartFields
                    }
                    order {
                      orderId
                      ...ReceiptFields
                    }
                    trackingInfo {
                      giftCardSalesValue
                      giftCardCode
                    }
                  }
                }
              }
            }
          `,
          ...sales.withCartFields(),
          ...sales.withReceiptFields()
        ),
        {
          url: `${(<any>command).protocol}//${(<any>command).host}${(<any>command).path}`,
          email: command.email || '',
          firstName: command.firstName || '',
          lastName: command.lastName || '',
          postalCode: command.postalCode || '',
          address1: command.address1 || '',
          address2: command.address2 || '',
          city: command.city || '',
          phone: command.phone || '',
          country: command.country || '',
          addressCode: command.addressCode || '',
          subscribedToNewsletter: command.subscribedToNewsletter || false,
        }
      );

      await this.cache.setItem('__cart', result.data.session.placeGetMyGiftOrder.placeGetMyGiftOrder.cart);
      return result.data.session.placeGetMyGiftOrder.placeGetMyGiftOrder;
    },
    'litium'
  ),
  getOrderReceiver: defineReceiver(
    commands.getOrder,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(
          gql`
            query getReceipt($url: String!, $orderId: String!) {
              session(url: $url) {
                receipt(orderId: $orderId) {
                  giftCards {
                    description
                  }
                  ...ReceiptFields
                  isBenifyOrder
                  benifyCheckoutImage
                }
              }
            }
          `,
          ...sales.withReceiptFields()
        ),
        {
          url: command.url,
          orderId: command.orderId,
        }
      );

      return result.data.session.receipt;
    },
    'litium'
  ),
  addBackInStockAlert: defineReceiver(
    commands.addBackInStockAlert,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(
          gql`
            mutation addBackInStockAlert($url: String!, $email: String!, $variantId: String!) {
              session(url: $url) {
                addBackInStockAlert {
                  addBackInStockAlert(email: $email, variantId: $variantId)
                }
              }
            }
          `
        ),
        {
          url: `${(<any>command).protocol}//${(<any>command).host}${(<any>command).path}`,
          email: command.email || '',
          variantId: command.variantId || '',
        }
      );

      return {
        error: result.data.session?.addBackInStockAlert.addBackInStockAlert,
      };
    },
    'litium'
  ),
  getOrderTracking: defineReceiver(
    commands.getOrderTracking,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(gql`
          mutation getOrderTracking($url: String!, $input: String!) {
            session(url: $url) {
              getOrderTracking{
                getOrderTracking(input: $input) {
                  error
                  reference
                  status
                  shipments {
                    agent
                    link
                    service
                    trackingNumber
                  }
                }
              }
            }
          }
        `),
        {
          url: `${(<any>command).protocol}//${(<any>command).host}${(<any>command).path}`,
          input: command.trackingId || '',
        }
      );

      return result.data.session.getOrderTracking.getOrderTracking;
    },
    'litium'
  ),
};
